/**
 * Small pieces the HRT / TRT tabs share, drawn from "HRT-TRT Patient View - screenshot.png": the numbered step
 * card, the Treatment Relevance box, the Lab / Clinic / Patient source chip, the round status mark, the radio and
 * the tick, the lavender chip, the "Not relevant now" fold and the WHY card. They follow the treatment builder's
 * own sizes (PatientWorkspace.tsx), so the hormone plan reads as the same product.
 */

import { useRef, useState, useEffect, type ReactNode } from 'react';
import { IconCheck, IconChevronDown, IconInfo } from './icons';
import { useTooltipShift } from './ui';
import { BAND_BOX, BAND_WORD, TIER_WORD, type HrtBand, type HrtEvidence, type HrtWhy, type ReadinessStatus } from '../lib/hrt';

/* ------------------------------------------------------------------ *
 * Step card
 * ------------------------------------------------------------------ */

export function StepNumber({ children }: { children: ReactNode }) {
  return (
    <span className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-full bg-forest font-display text-[17px] text-white">
      {children}
    </span>
  );
}

/** A white card with the design's numbered heading: "1  Choose a Hormone Pathway". */
export function StepCard({
  step,
  title,
  subtitle,
  action,
  children,
  testId,
}: {
  step?: number;
  title: string;
  subtitle?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <section className="rounded-[10px] border border-hairline bg-white px-3 pt-3 pb-3 sm:px-4" data-testid={testId}>
      <div className="flex items-start gap-3.5">
        {step !== undefined && <StepNumber>{step}</StepNumber>}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
            <h3 className="font-display text-[20px] leading-tight text-navy" style={{ fontWeight: 560 }}>{title}</h3>
            {action}
          </div>
          {subtitle && <p className="mt-1 text-[13.5px] leading-snug text-ink-muted">{subtitle}</p>}
        </div>
      </div>
      <div className="mt-3">{children}</div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Bands, chips and marks
 * ------------------------------------------------------------------ */

/** The design's "Treatment Relevance / High" box. A band word, never a number. */
export function BandBox({ band, compact = false }: { band: HrtBand; compact?: boolean }) {
  return (
    <span
      className={`inline-grid shrink-0 place-items-center rounded-[8px] text-center ${BAND_BOX[band]} ${
        compact ? 'min-w-[112px] px-2.5 py-1.5' : 'min-w-[132px] px-3 py-2 sm:min-w-[150px]'
      }`}
      data-testid="band-box"
    >
      <span className={`block leading-tight opacity-90 ${compact ? 'text-[10.5px]' : 'text-[11.5px]'}`}>Treatment Relevance</span>
      <span className={`block font-display leading-tight ${compact ? 'text-[16px]' : 'text-[21px]'}`} style={{ fontWeight: 600 }}>
        {BAND_WORD[band]}
      </span>
    </span>
  );
}

/** A data label in the hormone program's lavender: "Controlled: Schedule III", "HRT / TRT". */
export function HrtChip({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-[5px] bg-plum-soft px-[7px] py-[3px] text-[11.5px] font-semibold leading-none text-plum ${className}`}
    >
      {children}
    </span>
  );
}

/** Where a readiness line came from: Lab, Clinic or Patient. */
export function SourceChip({ source }: { source: string }) {
  return (
    <span className="shrink-0 rounded-[4px] bg-paper-deep px-1.5 py-[2px] text-[11px] font-semibold leading-none text-ink-muted">{source}</span>
  );
}

/** The round mark at the start of a readiness line. Shape and colour together, so it never relies on colour alone. */
export function StatusMark({ status }: { status: ReadinessStatus }) {
  if (status === 'ok') {
    return (
      <span className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full bg-forest text-white" aria-label="In order">
        <IconCheck size={13} />
      </span>
    );
  }
  if (status === 'locked') {
    return (
      <span className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full bg-alert text-white" aria-label="Locks a pathway">
        <LockGlyph size={12} />
      </span>
    );
  }
  if (status === 'missing') {
    return (
      <span className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full border border-dashed border-ink-muted text-ink-muted" aria-label="Missing">
        <span className="h-[2px] w-2 rounded bg-current" />
      </span>
    );
  }
  return (
    <span
      className={`grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full text-[13px] font-bold leading-none text-white ${
        status === 'review' ? 'bg-risk-yellow' : 'bg-ink-muted'
      }`}
      aria-label={status === 'review' ? 'Review needed' : 'Caution'}
    >
      !
    </span>
  );
}

export function LockGlyph({ size = 16, className = '' }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <rect x="5" y="10.6" width="14" height="9.8" rx="2" />
      <path d="M8.2 10.6V7.8a3.8 3.8 0 0 1 7.6 0v2.8" />
    </svg>
  );
}

/** The crosshair on the hormone program's goal box. */
export function CrosshairGlyph({ size = 40 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" stroke="var(--plum)" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
      <circle cx="20" cy="20" r="9.5" />
      <path d="M20 4.5v7M20 28.5v7M4.5 20h7M28.5 20h7" />
    </svg>
  );
}

export function TickBox({ checked, size = 26 }: { checked: boolean; size?: number }) {
  return (
    <span
      aria-hidden="true"
      style={{ width: size, height: size }}
      className={`grid shrink-0 place-items-center rounded-[5px] border ${
        checked ? 'border-forest bg-forest text-white' : 'border-hairline bg-white text-transparent'
      }`}
    >
      <IconCheck size={Math.round(size * 0.62)} />
    </span>
  );
}

export function RadioMark({ checked, disabled = false }: { checked: boolean; disabled?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`grid h-[26px] w-[26px] shrink-0 place-items-center rounded-full border-[2px] bg-white ${
        disabled ? 'border-hairline' : checked ? 'border-forest' : 'border-hairline'
      }`}
    >
      {checked && <span className="h-[12px] w-[12px] rounded-full bg-forest" />}
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Folds and hints
 * ------------------------------------------------------------------ */

/** "Not relevant now (1)": a heading-sized fold whose chevron turns as it opens. */
export function Fold({
  title,
  count,
  defaultOpen = false,
  icon,
  children,
  testId,
}: {
  title: string;
  count: number;
  defaultOpen?: boolean;
  icon?: ReactNode;
  children: ReactNode;
  testId?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mt-3" data-testid={testId}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        className="inline-flex items-center gap-2 rounded-md py-1 pr-2 text-left font-display text-[16.5px] text-navy hover:text-forest"
        style={{ fontWeight: 560 }}
      >
        <IconChevronDown size={17} className={`shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        {icon}
        {title} ({count})
      </button>
      <div className={`grid transition-all duration-200 ease-out ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
        {/* Closed, the rows are out of the tab order and hidden from screen readers too. */}
        <div className="overflow-hidden" inert={!open} aria-hidden={!open}>
          <div className="pt-2">{children}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * The design's "How the ranking works (i)" button: a bordered control whose explanation opens on hover, focus or
 * click, the same dark bubble as InfoHint, so the prose stays out of the layout.
 */
export function HintButton({ label, children, testId }: { label: string; children: ReactNode; testId?: string }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLSpanElement>(null);
  const tip = useTooltipShift();
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <span ref={box} className="group relative inline-flex shrink-0" onPointerEnter={tip.measure} onFocus={tip.measure}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          tip.measure();
          setOpen((was) => !was);
        }}
        className="inline-flex h-[30px] items-center gap-2 rounded-md border border-hairline bg-white px-3 text-[12.5px] text-navy hover:bg-mint"
        data-testid={testId}
      >
        {label}
        <IconInfo size={15} />
      </button>
      <span
        ref={tip.ref}
        style={tip.style}
        role="tooltip"
        className={`absolute right-0 top-full z-30 mt-2 w-[320px] max-w-[calc(100vw-2rem)] rounded-sm2 bg-ink px-3.5 py-3 text-[12px] leading-relaxed text-white shadow-lg transition-opacity duration-150 max-sm:left-1/2 max-sm:right-auto max-sm:-translate-x-1/2 ${
          open ? 'opacity-100' : 'pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100'
        }`}
      >
        {children}
      </span>
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * WHY card
 * ------------------------------------------------------------------ */

/** The spec's WHY card (tab "WHY Cards") with its evidence rows (tab "Evidence"), shown on demand. */
export function WhyCard({ why, evidence, title = 'Why this pathway' }: { why: HrtWhy; evidence: HrtEvidence[]; title?: string }) {
  return (
    <div className="mt-2 rounded-lg border border-hairline bg-paper px-3.5 py-3 text-[12.5px] leading-snug text-ink-muted" data-testid="why-card">
      <p className="font-display text-[14.5px] text-navy" style={{ fontWeight: 560 }}>{title}</p>
      <dl className="mt-1.5 space-y-1.5">
        <div>
          <dt className="text-[11px] font-semibold uppercase tracking-[0.05em] text-ink-muted">Why</dt>
          <dd>{why.why}</dd>
        </div>
        {why.evidence && (
          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-[0.05em] text-ink-muted">Evidence</dt>
            <dd>{why.evidence}</dd>
          </div>
        )}
        {why.before && (
          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-[0.05em] text-ink-muted">Before selecting</dt>
            <dd>{why.before}</dd>
          </div>
        )}
      </dl>
      {evidence.length > 0 && (
        <ul className="mt-2.5 space-y-1.5 border-t border-hairline pt-2">
          {evidence.map((row) => (
            <li key={row.id} className="flex gap-2">
              <span
                title={TIER_WORD[row.tier] ?? row.tier}
                className="mt-[1px] h-fit shrink-0 rounded-[4px] bg-white px-1.5 py-[1px] text-[10.5px] font-semibold text-ink-muted ring-1 ring-hairline"
              >
                {row.tier}
              </span>
              <span className="min-w-0">
                {row.url ? (
                  <a href={row.url} target="_blank" rel="noreferrer noopener" className="text-navy underline decoration-hairline underline-offset-2 hover:text-forest">
                    {row.citation}
                  </a>
                ) : (
                  <span className="text-navy">{row.citation}</span>
                )}
                <span className="text-ink-muted"> · {TIER_WORD[row.tier] ?? 'Source'}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** A short status line under a write: green when it went through, red with the API's words when it did not. */
export function SaveNote({ tone, children }: { tone: 'ok' | 'error' | 'warn'; children: ReactNode }) {
  return (
    <p
      role={tone === 'error' ? 'alert' : 'status'}
      className={`mt-2 rounded-md px-3 py-2 text-[13px] ${
        tone === 'ok' ? 'bg-mint text-forest' : tone === 'warn' ? 'bg-risk-yellow-soft text-risk-yellow' : 'bg-alert-soft text-alert'
      }`}
    >
      {children}
    </p>
  );
}

/** The dashed empty card the workspace uses for a tab with nothing yet. */
export function EmptyCard({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return (
    <section className="rounded-[10px] border border-dashed border-hairline bg-white px-5 py-8 text-center">
      <p className="font-display text-[18px] text-navy">{title}</p>
      <p className="mx-auto mt-1 max-w-md text-[13.5px] text-ink-muted">{body}</p>
      {action && <div className="mt-3 flex justify-center">{action}</div>}
    </section>
  );
}

/** A small text input in the workspace's own style. */
export const fieldClass =
  'w-full rounded-md border border-hairline bg-white px-3 py-2 text-[14px] font-normal text-navy placeholder:text-ink-muted focus:border-forest focus:outline-none disabled:bg-paper disabled:text-ink-muted';
